import { useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/** Get or create a stable anonymous viewer ID for unique view tracking */
const getViewerId = (userId?: string): string => {
  if (userId) return `user:${userId}`;
  let anonId = sessionStorage.getItem("encore_viewer_id");
  if (!anonId) {
    anonId = `anon:${crypto.randomUUID()}`;
    sessionStorage.setItem("encore_viewer_id", anonId);
  }
  return anonId;
};

/**
 * Hook to record unique views on posts.
 * Deduplicates via DB unique constraint + local Set to avoid redundant requests.
 */
export const useTrackView = () => {
  const { user } = useAuth();
  const tracked = useRef(new Set<string>());

  const trackView = useCallback(
    async (postId: string) => {
      if (!postId || postId.startsWith("f")) return; // skip fallback posts
      if (tracked.current.has(postId)) return;
      tracked.current.add(postId);

      const viewerId = getViewerId(user?.id);

      await supabase
        .from("post_views" as any)
        .insert({ post_id: postId, viewer_id: viewerId } as any)
        .then(({ error }) => {
          // Unique constraint violation = already viewed, ignore
          if (error && !error.message?.includes("duplicate")) {
            console.warn("View track error:", error.message);
          }
        });
    },
    [user?.id]
  );

  return trackView;
};
